import { Trans } from '@lingui/react/macro';
import { type PropsWithChildren, useState } from 'react';

import { type PageFiltersType } from '@/components/page';
import { NotesPanel } from '@/components/panels';
import { cn } from '@/lib/utils';

import DashboardWidgetActions from './DashboardWidgetActions';
import { type DashboardWidgetLibraryType, type DashboardWidgetType } from './types';

const isLibraryWidget = (widget: DashboardWidgetType): widget is DashboardWidgetLibraryType => {
  return 'library' in widget;
};

type Props = {
  widget: DashboardWidgetType;
  pageFilters: PageFiltersType;
  canFullScreen?: boolean;
  isFullScreen?: boolean;
  draggableHandleSelector?: string | false;
  draggableIgnoreSelector?: string | false;
  onWidgetExpand: (isExpanded: boolean, widget: DashboardWidgetType) => void;
};

const renderLibraryWidget = (widget: DashboardWidgetLibraryType, pageFilters: PageFiltersType) => {
  const { library: LibraryComponent } = widget;

  return <LibraryComponent pageFilters={pageFilters} />;
};

const DashboardWidget = ({
  widget,
  pageFilters,
  canFullScreen = true,
  isFullScreen = false,
  draggableHandleSelector,
  draggableIgnoreSelector,
  onWidgetExpand,
}: PropsWithChildren<Props>) => {
  const [isNotesDrawerOpen, setIsNotesDrawerOpen] = useState<boolean>(false);

  const handleWidgetExpand = (isExpanded: boolean) => {
    onWidgetExpand(isExpanded, widget);
  };

  return (
    <NotesPanel
      data-testid={`dashboard-widget-${widget.id}`}
      startDate={pageFilters.startDate}
      endDate={pageFilters.endDate}
      className={cn('h-full', { 'c99-pull-front': isNotesDrawerOpen })}
      headerClassName={
        draggableHandleSelector ? cn('cursor-move', draggableHandleSelector) : undefined
      }
      actionsClassName={
        draggableIgnoreSelector ? cn('cursor-default', draggableIgnoreSelector) : undefined
      }
      title={widget.title}
      hideNotes={!widget.showNotes}
      infoPopover={widget.infoPopover}
      actions={
        <DashboardWidgetActions
          link={widget.link}
          canExpand={canFullScreen}
          isExpanded={isFullScreen}
          onExpandToggle={handleWidgetExpand}
        />
      }
      noPadding
      verifyC99Tag={widget.verifyC99Tag}
      onDrawerOpenChange={setIsNotesDrawerOpen}
    >
      {isLibraryWidget(widget) ? (
        renderLibraryWidget(widget, pageFilters)
      ) : (
        <div>
          <Trans>Dynamic widgets coming soon...</Trans>
        </div>
      )}
    </NotesPanel>
  );
};

export default DashboardWidget;
